import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, Calendar, Zap, Mail, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loaded, setLoaded] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const { t, i18n } = useTranslation();
  const [glitchActive, setGlitchActive] = useState(false);

  useEffect(() => {
    setLoaded(true);
    
    // Random glitch effect
    const glitchInterval = setInterval(() => {
      setGlitchActive(true);
      setTimeout(() => setGlitchActive(false), 200);
    }, 8000);
    
    return () => clearInterval(glitchInterval);
  }, []);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-50% 0px -50% 0px',
      threshold: 0
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    const sections = ['home', 'schedule', 'register'];
    sections.forEach((id) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  const toggleLanguage = () => {
    const currentLang = i18n.resolvedLanguage || i18n.language || 'en';
    const isArabic = currentLang.toLowerCase().startsWith('ar');
    const newLang = isArabic ? 'en' : 'ar';
    i18n.changeLanguage(newLang);
    // Keep overall layout stable (avoid RTL flipping scrollbars/layout). We only change language.
    document.dir = 'ltr';
    if (document.documentElement) {
      document.documentElement.lang = newLang;
      document.documentElement.dir = 'ltr';
    }
  };

  const smoothScroll = (e, targetId) => {
    e.preventDefault();
    
    // If not on home page, navigate to home first
    if (location.pathname !== '/') {
      navigate('/');
      // Wait for page to load then scroll
      setTimeout(() => {
        const element = document.getElementById(targetId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    } else {
      // Already on home page, just scroll
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const navItems = [
    { id: 'home', label: t('home'), icon: Home, section: 'home' },
    { id: 'about', label: t('about'), icon: Info, section: 'about' },
    { id: 'schedule', label: t('schedule'), icon: Calendar, section: 'schedule' },
    { id: 'register', label: t('register'), icon: Zap, section: 'register' },
    { id: 'contact', label: t('contact'), icon: Mail, section: 'contact' },
  ];

  const isActive = (id) => activeSection === id;

  return (
    <>
      <style>{`
        @keyframes scanline {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100vh); }
        }
        
        @keyframes glitch {
          0% { transform: translate(0); }
          20% { transform: translate(-2px, 2px); }
          40% { transform: translate(-2px, -2px); }
          60% { transform: translate(2px, 2px); }
          80% { transform: translate(2px, -2px); }
          100% { transform: translate(0); }
        }
        
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 5px #00ffff, 0 0 10px #00ffff, 0 0 15px #00ffff; }
          50% { box-shadow: 0 0 10px #00ffff, 0 0 20px #00ffff, 0 0 30px #00ffff, 0 0 40px #00ffff; }
        }
        
        @keyframes border-flow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        .scanline {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(transparent, #00ffff 50%, transparent);
          animation: scanline 4s linear infinite;
          pointer-events: none;
          z-index: 10;
        }
        
        .glitch-text {
          position: relative;
        }
        
        .glitch-text.active::before,
        .glitch-text.active::after {
          content: attr(data-text);
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          height: 100%;
        }
        
        .glitch-text.active::before {
          animation: glitch 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94) infinite;
          color: #ff00ff;
          z-index: -1;
        }
        
        .glitch-text.active::after {
          animation: glitch 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94) reverse infinite;
          color: #00ffff;
          z-index: -2;
        }
        
        .cyber-corners {
          clip-path: polygon(
            0 8px, 8px 0, calc(100% - 8px) 0, 100% 8px,
            100% calc(100% - 8px), calc(100% - 8px) 100%, 8px 100%, 0 calc(100% - 8px)
          );
        }
        
        .cyber-border {
          position: relative;
        }
        
        .cyber-border::before {
          content: '';
          position: absolute;
          inset: -2px;
          background: linear-gradient(45deg, #00ffff, #ff00ff, #ffff00, #00ffff);
          background-size: 300% 300%;
          animation: border-flow 3s ease infinite;
          clip-path: polygon(
            0 8px, 8px 0, calc(100% - 8px) 0, 100% 8px,
            100% calc(100% - 8px), calc(100% - 8px) 100%, 8px 100%, 0 calc(100% - 8px)
          );
          z-index: -1;
        }
        
        .hud-corner {
          position: absolute;
          width: 12px;
          height: 12px;
          border: 2px solid #00ffff;
        }
        
        .hud-corner.top-left {
          top: -1px;
          left: -1px;
          border-right: none;
          border-bottom: none;
        }
        
        .hud-corner.top-right {
          top: -1px;
          right: -1px;
          border-left: none;
          border-bottom: none;
        }
        
        .hud-corner.bottom-left {
          bottom: -1px;
          left: -1px;
          border-right: none;
          border-top: none;
        }
        
        .hud-corner.bottom-right {
          bottom: -1px;
          right: -1px;
          border-left: none;
          border-top: none;
        }
        
        .neon-glow {
          text-shadow: 
            0 0 5px #00ffff,
            0 0 10px #00ffff,
            0 0 20px #00ffff,
            0 0 40px #00ffff;
        }
        
        .grid-bg {
          background-image: 
            linear-gradient(rgba(0, 255, 255, 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 255, 255, 0.05) 1px, transparent 1px);
          background-size: 20px 20px;
        }
      `}</style>

      {/* Language Toggle - Desktop Top Right Corner */}
      <motion.button
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        onClick={toggleLanguage}
        className="hidden lg:flex fixed top-4 right-4 z-50 group cyber-corners border-2 border-cyan-500/30 hover:border-cyan-500 bg-black/95 backdrop-blur-xl p-3 transition-all duration-300 hover:bg-cyan-500/10"
        style={{
          boxShadow: '0 0 15px rgba(0, 255, 255, 0.3)'
        }}
        data-testid="desktop-language-toggle"
      >
        <div className="relative h-6 w-12 flex items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.span
              key={i18n.resolvedLanguage || i18n.language}
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -10, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="absolute inset-0 flex items-center justify-center font-bold text-sm tracking-wider text-cyan-400 group-hover:text-cyan-300"
            >
              {(i18n.resolvedLanguage || i18n.language || 'en').toLowerCase().startsWith('ar') ? 'AR' : 'EN'}
            </motion.span>
          </AnimatePresence>
        </div>
        
        {/* Tooltip */}
        <div className="absolute right-full mr-4 top-1/2 -translate-y-1/2 px-4 py-2 bg-black/95 border-2 border-cyan-500/50 backdrop-blur-xl opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-300 whitespace-nowrap z-50 cyber-corners">
          <span className="text-xs font-mono uppercase tracking-wider text-cyan-400">
            Language
          </span>
          {/* Data indicator */}
          <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-2 h-2 bg-cyan-500" style={{ boxShadow: '0 0 5px #00ffff' }} />
          {/* Corner accents */}
          <div className="absolute top-0 left-0 w-2 h-2 border-t-2 border-l-2 border-cyan-500" />
          <div className="absolute bottom-0 right-0 w-2 h-2 border-b-2 border-r-2 border-purple-500" />
        </div>
      </motion.button>

      {/* Vertical Sidebar */}
      <motion.aside
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="fixed left-0 top-0 h-screen w-20 lg:w-24 bg-black/95 backdrop-blur-xl border-r-2 border-cyan-500/50 z-50 flex flex-col items-center py-8 hidden lg:flex grid-bg overflow-hidden"
        data-testid="vertical-sidebar"
        style={{
          boxShadow: 'inset 0 0 50px rgba(0, 255, 255, 0.1), 0 0 20px rgba(0, 255, 255, 0.2)'
        }}
      >
        {/* Scanline Effect */}
        <div className="scanline" />
        
        {/* HUD Corners */}
        <div className="hud-corner top-left" />
        <div className="hud-corner top-right" />
        <div className="hud-corner bottom-left" />
        <div className="hud-corner bottom-right" />

        {/* Logo */}
            <Link 
                  to="/" 
                  className="mb-12 group relative flex justify-center"
                  data-testid="sidebar-logo"
                >
                  {/* Clean Logo Container without background/css effects */}
                  <div className="relative w-12 h-12 lg:w-16 lg:h-16 flex items-center justify-center transform group-hover:scale-110 transition-all duration-300">
                    <img 
                      src="/assets/logo.png" 
                      alt="Logo" 
                      className="w-full h-full object-contain" 
                    />
                  </div>
                </Link>
          
          {/* Data bars */}
          <div className="absolute -right-1 top-0 w-1 h-full flex flex-col justify-around">
            <div className="w-1 h-2 bg-cyan-500" style={{ boxShadow: '0 0 5px #00ffff' }} />
            <div className="w-1 h-3 bg-purple-500" style={{ boxShadow: '0 0 5px #ff00ff' }} />
            <div className="w-1 h-2 bg-yellow-500" style={{ boxShadow: '0 0 5px #ffff00' }} />
          </div>

        {/* Navigation */}
        <nav className="flex-1 flex flex-col items-center space-y-8">
          {navItems.map((item, index) => (
            <motion.a
              key={item.id}
              href={`#${item.section}`}
              onClick={(e) => smoothScroll(e, item.section)}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
              className={`group relative p-4 cyber-corners transition-all duration-300 ${
                activeSection === item.section
                  ? 'bg-cyan-500/20 border-2 border-cyan-500'
                  : 'border-2 border-cyan-500/20 hover:border-cyan-500 hover:bg-cyan-500/10'
              }`}
              style={activeSection === item.section ? {
                boxShadow: '0 0 20px rgba(0, 255, 255, 0.5), inset 0 0 20px rgba(0, 255, 255, 0.1)',
                animation: 'pulse-glow 2s ease-in-out infinite'
              } : {}}
              data-testid={`nav-${item.id}`}
            >
              <item.icon 
                className={`h-6 w-6 ${
                  activeSection === item.section 
                    ? 'text-cyan-400 neon-glow' 
                    : 'text-cyan-500/70 group-hover:text-cyan-400'
                }`} 
              />
              
              {/* Tooltip */}
              <div className="absolute left-full ml-4 px-4 py-2 bg-black/95 border-2 border-cyan-500/50 backdrop-blur-xl opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-300 whitespace-nowrap z-50 cyber-corners">
                <span 
                  className={`text-xs font-mono uppercase tracking-wider text-cyan-400 glitch-text ${glitchActive ? 'active' : ''}`}
                  data-text={item.label}
                >
                  {item.label}
                </span>
                {/* Data indicator */}
                <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 bg-cyan-500" style={{ boxShadow: '0 0 5px #00ffff' }} />
                {/* Corner accents */}
                <div className="absolute top-0 right-0 w-2 h-2 border-t-2 border-r-2 border-cyan-500" />
                <div className="absolute bottom-0 left-0 w-2 h-2 border-b-2 border-l-2 border-purple-500" />
              </div>
              
              {/* Active indicator line */}
              {activeSection === item.section && (
                <motion.div
                  layoutId="activeIndicator"
                  className="absolute -left-1 top-0 w-1 h-full bg-cyan-500"
                  style={{ boxShadow: '0 0 10px #00ffff' }}
                />
              )}
            </motion.a>
          ))}
        </nav>


      </motion.aside>

      {/* Mobile Top Bar - Cyberpunk Style */}
      <motion.div
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-black/95 backdrop-blur-xl border-b-2 border-cyan-500/50 z-40 flex items-center justify-between px-4 overflow-hidden"
        dir="ltr"
        style={{
          boxShadow: '0 0 20px rgba(0, 255, 255, 0.2)'
        }}
      >
        <div className="scanline" />
        
        <Link to="/" className="flex items-center space-x-3 relative z-10">
          {/* Clean Logo Image directly, no background container */}
          <img 
            src="/assets/logo.png" 
            alt="Logo" 
            className="h-10 w-auto object-contain" 
          />
          <span className="text-sm font-mono uppercase tracking-wider text-cyan-400 font-bold neon-glow">
            {t('brandName')}
          </span>
        </Link>
        
        <div className="flex items-center space-x-2 relative z-10">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.id);
            
            return (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={(e) => smoothScroll(e, item.id)}
                data-testid={`mobile-${item.label.toLowerCase()}`}
                className={`p-2 cyber-corners transition-all ${
                  active 
                    ? 'text-cyan-400 bg-cyan-500/20 border border-cyan-500' 
                    : 'text-cyan-500/70 hover:text-cyan-400 border border-transparent'
                }`}
                style={active ? {
                  boxShadow: '0 0 10px rgba(0, 255, 255, 0.5)'
                } : {}}
              >
                <Icon className="h-5 w-5" />
              </a>
            );
          })}
          <button
            onClick={toggleLanguage}
            className="p-2 text-cyan-500/70 hover:text-cyan-400 transition-colors flex flex-col items-center cyber-corners border border-cyan-500/20"
          >
            <span className="text-xs uppercase font-mono mb-1">Lang</span>
            <div className="relative h-5 w-10 overflow-hidden flex items-center justify-center">
              <AnimatePresence mode="wait">
                <motion.span
                  key={i18n.language}
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -10, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="absolute inset-0 flex items-center justify-center font-bold text-sm tracking-wider"
                >
                  {i18n.language === 'en' ? 'EN' : 'AR'}
                </motion.span>
              </AnimatePresence>
            </div>
          </button>
        </div>
      </motion.div>
    </>
  );
};

export default Header;